import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../services/supabaseAudioService';
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import paymentService from '../services/paymentService';
import './GoogleAuth.css';

import { Picto } from './common/Picto';
interface GoogleAuthProps {
  onLogin: (userData: any) => void;
  onLogout?: () => void;
  isHeader?: boolean;
}

const GoogleAuth: React.FC<GoogleAuthProps> = ({ onLogin, onLogout, isHeader = false }) => {
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avatarFailed, setAvatarFailed] = useState(false);

  // Réinitialise l'état d'échec de l'avatar quand l'URL change (ex. reconnexion)
  useEffect(() => {
    setAvatarFailed(false);
  }, [user?.picture]);

  // Ref toujours à jour avec la dernière valeur de onLogin.
  // Permet de ne pas recréer la subscription onAuthStateChange quand
  // handleLogin (App.tsx) obtient une nouvelle référence, ce qui évite
  // que INITIAL_SESSION ne se redéclenche et n'efface l'historique.
  const onLoginRef = useRef(onLogin);
  onLoginRef.current = onLogin;

  // ✅ Fonction pour gérer le deep link
  const handleDeepLink = async (url: string) => {
    // Retour de Stripe Checkout (voir paymentService.redirectToCheckout)
    if (url.startsWith('com.jogolinga.com://payment-success')) {
      try {
        await Browser.close();
      } catch (e) {
        // Navigateur déjà fermé
      }

      const sessionId = new URL(url).searchParams.get('session_id');
      if (sessionId) {
        paymentService.verifyPayment(sessionId).catch(() => {});
      }
      return;
    }

    if (url.startsWith('com.jogolinga.com://payment-cancelled')) {
      try {
        await Browser.close();
      } catch (e) {
        // Navigateur déjà fermé
      }
      return;
    }

    // Vérifier si c'est un callback OAuth
    if (url.includes('access_token') || url.includes('code=')) {
      // Fermer le navigateur in-app s'il est ouvert
      try {
        await Browser.close();
      } catch (e) {
        // Navigateur déjà fermé
      }
      
      // ✅ Extraire les paramètres OAuth de l'URL
      const hashIndex = url.indexOf('#');
      if (hashIndex !== -1) {
        const hash = url.substring(hashIndex + 1);
        
        // ✅ Parser les paramètres de l'URL
        const params = new URLSearchParams(hash);
        const access_token = params.get('access_token');
        const refresh_token = params.get('refresh_token');
        
        if (access_token && refresh_token) {
          try {
            // ✅ Appeler Supabase pour créer la session manuellement
            const { error } = await supabase.auth.setSession({
              access_token: access_token,
              refresh_token: refresh_token
            });
            
            if (error) {
              throw error;
            }
          } catch (error: any) {
}
        }
      }
    }
  };

  // Vérifier la session au chargement
  useEffect(() => {
    checkSession();
    
    // Écouter les changements d'auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          const userData = {
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata.full_name || session.user.email,
            picture: session.user.user_metadata.avatar_url || session.user.user_metadata.picture
          };

          setUser(userData);
          setIsLoggedIn(true);

          // N'appeler onLogin que pour SIGNED_IN (vraie connexion).
          // INITIAL_SESSION est géré par checkSession() qui utilise le ref stable.
          // TOKEN_REFRESHED / USER_UPDATED ne déclenchent plus handleLogin,
          // ce qui évite clearProgressLocalStorage à chaque rafraîchissement JWT.
          if (event === 'SIGNED_IN') {
            try {
              onLoginRef.current({
                ...userData,
                accessToken: session.access_token,
                jwtToken: session.access_token
              });
            } catch (loginError) {
}
          }
        } else {
          setUser(null);
          setIsLoggedIn(false);
        }

        setIsLoading(false);
      }
    );

    // ✅ Configuration deep links pour Capacitor
    let appUrlListener: any = null;
    
    if (Capacitor.isNativePlatform()) {
      // ✅ Vérifier immédiatement s'il y a un deep link au démarrage
      CapApp.getLaunchUrl().then(result => {
        if (result && result.url) {
          handleDeepLink(result.url);
        }
      }).catch(() => {
        // Pas de deep link au démarrage
      });
      
      // ✅ Écouter les futurs deep links
      CapApp.addListener('appUrlOpen', async (event) => {
        await handleDeepLink(event.url);
      });
      
      appUrlListener = true;
    }

    return () => {
      subscription.unsubscribe();
      if (appUrlListener && Capacitor.isNativePlatform()) {
        CapApp.removeAllListeners();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        const userData = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata.full_name || session.user.email,
          picture: session.user.user_metadata.avatar_url || session.user.user_metadata.picture
        };

        setUser(userData);
        setIsLoggedIn(true);

        // Appeler onLogin pour la session initiale (équivalent de INITIAL_SESSION)
        try {
          onLoginRef.current({
            ...userData,
            accessToken: session.access_token,
            jwtToken: session.access_token
          });
        } catch (loginError) {
}
      }
    } catch (error) {
} finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const isNative = Capacitor.isNativePlatform();
      
      // Définir l'URL de redirection
      const redirectUrl = isNative 
        ? 'com.jogolinga.com://login-callback'
        : window.location.origin;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          skipBrowserRedirect: isNative,
        },
      });

      if (error) {
        throw error;
      }

      // ✅ Sur plateforme native, ouvrir le navigateur in-app
      if (isNative && data?.url) {
        await Browser.open({ 
          url: data.url,
          windowName: '_blank',
          presentationStyle: 'fullscreen',
          toolbarColor: '#4285F4',
        });
        
        setIsLoading(false);
      }
      
    } catch (error: any) {
setError(error.message || t('googleAuth.connectionError'));
      setIsLoading(false);
    }
  };

  // Déconnexion — le signOut Supabase est géré par handleLogout (onLogout).
  // On nettoie l'état local quoi qu'il arrive pour ne pas bloquer l'utilisateur.
  const handleSignOut = async () => {
    setUser(null);
    setIsLoggedIn(false);
    onLogout?.();
  };

  const clearError = () => setError(null);

  // Affichage loading
  if (isLoading && !isLoggedIn) {
    return (
      <div className={isHeader ? "header-auth-loading" : "google-login-button"} style={{ cursor: 'default' }}>
        {t('googleAuth.loading')}
      </div>
    );
  }

  // Rendu pour l'en-tête
  if (isHeader) {
    return (
      <div className="header-auth-container">
        {isLoggedIn ? (
          <button
            onClick={handleSignOut}
            className="header-logout-button"
            type="button"
            aria-label={t('googleAuth.signOut')}
          >
            {user?.picture && !avatarFailed ? (
              <img
                src={user.picture}
                alt=""
                className="header-avatar"
                onError={() => setAvatarFailed(true)}
              />
            ) : (
              <span className="header-avatar header-avatar--initials">
                {(user?.name || user?.email || '?')[0].toUpperCase()}
              </span>
            )}
            <span className="header-logout-icon">↪</span>
          </button>
        ) : (
          <button
            onClick={handleGoogleSignIn}
            className="header-login-button"
            type="button"
            aria-label={t('googleAuth.signInWithGoogle')}
          >
            <svg width="14" height="14" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
              <path fill="#fff" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"/>
              <path fill="#fff" d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"/>
              <path fill="#fff" d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"/>
              <path fill="#fff" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z"/>
            </svg>
            <span>{t('googleAuth.signIn')}</span>
          </button>
        )}
      </div>
    );
  }

  // Rendu principal (page de connexion complète)
  return (
    <div className="login-container">
      {error && (
        <div className="auth-error" style={{ 
          background: '#fee', 
          color: '#c33', 
          padding: '10px', 
          marginBottom: '15px', 
          borderRadius: '4px',
          border: '1px solid #fcc'
        }}>
          <Picto name="attention" /> {error}
          <button 
            onClick={clearError}
            style={{ 
              float: 'right', 
              background: 'transparent', 
              border: 'none', 
              color: '#c33',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {isLoggedIn && user ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '20px' }}>
            {user.picture && !avatarFailed && (
              <img
                src={user.picture}
                alt="Profile"
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  marginBottom: '10px'
                }}
                onError={() => setAvatarFailed(true)}
              />
            )}
            <div>
              <strong>{user.name}</strong>
              <div style={{ fontSize: '14px', color: '#666' }}>{user.email}</div>
            </div>
          </div>
          
          <button
            onClick={handleSignOut}
            className="google-login-button"
            style={{ backgroundColor: '#dc3545' }}
          >
            {t('googleAuth.signOut')}
          </button>
        </div>
      ) : (
        <button
          onClick={handleGoogleSignIn}
          className="google-login-button"
        >
          {t('googleAuth.signInWithGoogle')}
        </button>
      )}
    </div>
  );
};

export default GoogleAuth;